import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { COMPANY, CONTACT_CTA } from "@/lib/content";
import { imageProps } from "@/lib/images";
import { cn } from "@/lib/utils";

/* ---------- Image: responsive WebP + blur placeholder, fades in when loaded ---------- */
export const Img = ({ className = "", src, sizes, style, loading, fetchPriority, ...props }) => {
  const [ok, setOk] = useState(false);
  const ref = useRef(null);
  const r = imageProps(src, sizes);
  useEffect(() => {
    if (ref.current?.complete && ref.current?.naturalWidth > 0) setOk(true);
  }, [r.src]);
  const eager = loading === "eager" || fetchPriority === "high";
  // With a blur placeholder we keep the element visible so the placeholder shows while the real pixels arrive.
  const placeholder = r.lqip && !ok ? { backgroundImage: `url("${r.lqip}")`, backgroundSize: "cover", backgroundPosition: style?.objectPosition || "center" } : undefined;
  return (
    <img
      ref={ref}
      src={r.src}
      srcSet={r.srcSet}
      sizes={r.srcSet ? r.sizes : undefined}
      width={r.width}
      height={r.height}
      onLoad={() => setOk(true)}
      decoding={eager ? "sync" : "async"}
      loading={loading || (eager ? "eager" : "lazy")}
      fetchPriority={fetchPriority || (eager ? "high" : "auto")}
      style={{ ...style, ...placeholder }}
      className={cn("transition-opacity duration-500 ease-out", ok || r.lqip ? "opacity-100" : "opacity-0", className)}
      {...props}
    />
  );
};

/* ---------- Buttons ---------- */
const BASE =
  "link-arrow inline-flex shrink-0 select-none items-center justify-center gap-2 rounded-[10px] font-medium transition-[background-color,border-color,color,transform,box-shadow] duration-150 active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50";
const VARIANTS = {
  primary: "bg-brand text-brand-foreground hover:bg-brand-dark",
  dark: "bg-foreground text-background hover:bg-foreground/90",
  light: "bg-white text-foreground hover:bg-white/90",
  lime: "bg-brand text-brand-foreground hover:bg-brand-dark",
  outline: "border border-foreground/20 bg-transparent text-foreground hover:border-foreground hover:bg-foreground/[0.03]",
  outlineLight: "border border-white/30 text-white hover:border-white hover:bg-white/10",
  ghost: "text-foreground hover:bg-foreground/[0.05]",
  soft: "bg-secondary text-foreground hover:bg-accent",
};
const SIZES = { sm: "h-9 px-3.5 text-sm", md: "h-11 px-5 text-[15px]", lg: "h-12 px-6 text-base" };

export const Btn = ({ to, href, variant = "primary", size = "md", className = "", children, arrow = false, ...rest }) => {
  const cls = cn(BASE, VARIANTS[variant], SIZES[size], className);
  const content = (
    <>
      {children}
      {arrow && <ArrowRight className="h-4 w-4" strokeWidth={2} />}
    </>
  );
  if (to) return <Link to={to} className={cls} {...rest}>{content}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{content}</a>;
  return <button type="button" className={cls} {...rest}>{content}</button>;
};

export const TextLink = ({ to, href, children, className = "", external = false, light = false, ...rest }) => {
  const cls = cn("link-arrow inline-flex items-center gap-1.5 text-[15px] font-medium", light ? "text-white/85 hover:text-white" : "text-foreground hover:text-brand", className);
  const Icon = external ? ArrowUpRight : ArrowRight;
  const inner = (<>{children}<Icon className="h-4 w-4" strokeWidth={2} /></>);
  if (href) return <a href={href} className={cls} {...rest}>{inner}</a>;
  return <Link to={to} className={cls} {...rest}>{inner}</Link>;
};

/* ---------- Diagonal arrow used on tiles ---------- */
export const ArrowBadge = ({ className = "", light = false }) => (
  <ArrowUpRight aria-hidden className={cn("mt-1.5 h-5 w-5 shrink-0 transition-colors", light ? "text-white/50 group-hover:text-white" : "text-foreground/30 group-hover:text-foreground", className)} />
);

/* ---------- Small label above a title ---------- */
export const Eyebrow = ({ children, light = false, className = "", ...rest }) => (
  <p className={cn("text-[13px] font-medium", light ? "text-white/55" : "text-muted-foreground", className)} {...rest}>{children}</p>
);

/* ---------- Small tag ---------- */
export const Tag = ({ children, className = "", dark = false, ...rest }) => (
  <span className={cn("inline-flex h-7 items-center rounded-md px-2.5 text-[13px] font-medium", dark ? "bg-white/10 text-white" : "bg-secondary text-foreground/80", className)} {...rest}>{children}</span>
);

/* ---------- Content notice: one quiet caption line, never a banner ---------- */
export const DemoNote = ({ children, className = "", testid = "public-demo-note" }) => (
  <p className={cn("caption max-w-2xl", className)} data-testid={testid}>{children}</p>
);

/* ---------- Divider ---------- */
export const DimLine = ({ className = "", light = false }) => <div aria-hidden className={cn("h-px w-full", light ? "bg-white/15" : "bg-border", className)} />;

export const PhotoFlag = () => null;

/* ---------- Photo / Figure ---------- */
export const Photo = ({ src, alt = "", caption, className = "", imgClass = "", ratio = "aspect-[16/10]", priority = false, children, rounded = "rounded-2xl", radius, dark = false, flag, sizes, ...rest }) => (
  <figure className={cn("min-w-0", className)} {...rest}>
    <div className={cn("relative overflow-hidden", radius || rounded, dark ? "bg-white/5" : "bg-muted", ratio)}>
      <Img src={src} alt={alt} sizes={sizes} loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : undefined} className={cn("img-premium h-full w-full object-cover", imgClass)} />
      {children}
    </div>
    {caption && <figcaption className={cn("mt-3 text-sm", dark ? "text-white/50" : "text-muted-foreground")}>{caption}</figcaption>}
  </figure>
);
export const Figure = Photo;

/* ---------- Page head: title left, lead and actions right ---------- */
export const PageHead = ({ eyebrow, title, lead, actions, children, className = "", titleClass = "", testid = "public-page-head" }) => (
  <section data-testid={testid} className={cn("container-public pt-10 sm:pt-14 lg:pt-20", className)}>
    <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
      <div className="lg:col-span-8">
        {eyebrow && <Eyebrow className="mb-5">{eyebrow}</Eyebrow>}
        <h1 className={cn("t-h1 max-w-4xl", titleClass)}>{title}</h1>
      </div>
      {(lead || actions) && (
        <div className="flex flex-col justify-end gap-6 lg:col-span-4">
          {lead && <p className="t-lead max-w-md">{lead}</p>}
          {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
        </div>
      )}
    </div>
    {children}
  </section>
);

/* ---------- Section head: title left, optional text/action right ---------- */
export const SectionHead = ({ eyebrow, title, text, action, className = "", dark = false, as: As = "h2", id }) => (
  <div className={cn("flex flex-col gap-5 md:flex-row md:items-end md:justify-between", className)}>
    <div>
      {eyebrow && <Eyebrow light={dark} className="mb-4">{eyebrow}</Eyebrow>}
      <As id={id} className={cn("t-h2 max-w-2xl", dark && "text-white")}>{title}</As>
    </div>
    {(text || action) && (
      <div className="flex flex-col items-start gap-3 md:items-end">
        {text && <p className={cn("max-w-md text-[15px] leading-relaxed md:text-right", dark ? "text-white/60" : "text-muted-foreground")}>{text}</p>}
        {action}
      </div>
    )}
  </div>
);

/* ---------- Key numbers ---------- */
export const Facts = ({ items, className = "", cols = "grid-cols-2 lg:grid-cols-4", big = false, dark = false, ...rest }) => (
  <dl className={cn("grid gap-x-8 gap-y-10", cols, className)} {...rest}>
    {items.map(([value, label]) => (
      <div key={label} className={cn("border-t pt-5", dark ? "border-white/15" : "border-border")}>
        <dd className={cn("t-num", dark && "text-white", big ? "text-5xl sm:text-6xl lg:text-7xl" : "text-[2.5rem] sm:text-5xl")}>{value}</dd>
        <dt className={cn("mt-2 max-w-[22ch] text-[15px] leading-snug", dark ? "text-white/60" : "text-muted-foreground")}>{label}</dt>
      </div>
    ))}
  </dl>
);

/* ---------- Simple list rows ---------- */
export const Rows = ({ children, className = "", dark = false }) => (
  <ul className={cn("divide-y border-y", dark ? "divide-white/10 border-white/10" : "divide-border border-border", className)}>{children}</ul>
);

/* ---------- Check list ---------- */
export const Checks = ({ items, className = "", dark = false }) => (
  <ul className={cn("space-y-3", className)}>
    {items.map((t) => (
      <li key={t} className="flex items-start gap-3 text-[15px] leading-relaxed">
        <span className={cn("mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center", dark ? "text-white" : "text-brand")}><Check className="h-4 w-4" strokeWidth={2.5} /></span>
        <span className={dark ? "text-white/85" : ""}>{t}</span>
      </li>
    ))}
  </ul>
);

/* ---------- Form primitives ---------- */
export const Field = ({ label, hint, htmlFor, children, className = "", error }) => (
  <div className={cn("min-w-0", className)}>
    <label htmlFor={htmlFor} className="mb-2 flex items-baseline justify-between text-sm font-medium">
      <span>{label}</span>
      {hint && <span className="text-[13px] font-normal text-muted-foreground">{hint}</span>}
    </label>
    {children}
    {error && <p className="mt-2 text-[13px] text-destructive">{error}</p>}
  </div>
);

/* Segmented control */
export const Choice = ({ options, value, onChange, name, testid }) => (
  <div role="radiogroup" aria-label={name} className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid={testid}>
    {options.map(([k, l]) => {
      const on = value === k;
      return (
        <button
          key={k}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onChange(k)}
          data-testid={`${testid}-option-${k}`}
          className={cn(
            "inline-flex h-11 items-center justify-center rounded-[10px] border px-4 text-[15px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            on ? "border-foreground bg-foreground text-background" : "border-input bg-background text-foreground/80 hover:border-foreground/60 hover:text-foreground"
          )}
        >
          {l}
        </button>
      );
    })}
  </div>
);

/* ---------- Price rows ---------- */
export const PriceRow = ({ item, compact = false, mini = false }) => {
  if (mini) {
    return (
      <li className="flex items-baseline justify-between gap-4 py-3.5" data-testid={`price-row-${item.id}`}>
        <Link to={`/kontakt?teneste=${encodeURIComponent(item.id)}`} className="u-link min-w-0 truncate text-[15.5px] font-medium" data-testid={`price-order-${item.id}`}>{item.name}</Link>
        <span className="t-num shrink-0 text-[15px]">{item.price}</span>
      </li>
    );
  }
  return (
    <li className={cn("grid gap-3 py-5 sm:grid-cols-12 sm:items-start sm:gap-6", compact && "py-4")} data-testid={`price-row-${item.id}`}>
      <div className="min-w-0 sm:col-span-6">
        <p className="text-[17px] font-medium leading-snug">{item.name}</p>
        {!compact && <p className="mt-1.5 max-w-[52ch] text-[15px] leading-relaxed text-muted-foreground">{item.desc}</p>}
      </div>
      <div className="sm:col-span-4">
        <p className="t-num text-[22px] leading-none sm:text-2xl">{item.price}</p>
        <p className="mt-1.5 text-[13px] text-muted-foreground">{item.unit}</p>
      </div>
      <div className="sm:col-span-2 sm:justify-self-end">
        <Btn to={`/kontakt?teneste=${encodeURIComponent(item.id)}`} variant="outline" size="sm" data-testid={`price-order-${item.id}`}>Bestill</Btn>
      </div>
    </li>
  );
};

/* ---------- Closing contact block (dark) ---------- */
export const CTA = ({ company = COMPANY, label = CONTACT_CTA.label, title = CONTACT_CTA.title, text = CONTACT_CTA.text, sub = CONTACT_CTA.sub, button = CONTACT_CTA.button || "Send førespurnad", testid = "public-cta-section" }) => (
  <section className="container-public" data-testid={testid}>
    <div className="grid gap-10 rounded-3xl bg-ink px-6 py-12 text-white sm:px-10 sm:py-16 lg:grid-cols-12 lg:gap-12 lg:px-14 lg:py-20">
      <div className="lg:col-span-7">
        {label && <p className="mb-4 text-[13px] font-medium text-white/55">{label}</p>}
        <h2 className="t-h2 text-white">{title}</h2>
        <p className="mt-5 max-w-lg text-[17px] leading-relaxed text-white/65">{text}</p>
        {sub ? <p className="mt-2 max-w-lg text-[17px] leading-relaxed text-white/65">{sub}</p> : null}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Btn to="/kontakt" size="lg" arrow data-testid="public-cta-bestill-button">{button}</Btn>
          <Btn href={company.phoneHref} variant="outlineLight" size="lg" data-testid="public-cta-phone-link">Ring {company.phone}</Btn>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-7 text-[15px] lg:col-span-5 lg:pt-1">
        <div>
          <dt className="text-white/50">Hovudkontor</dt>
          <dd className="mt-1 text-white/90">{company.hq.visit.map((l) => <span key={l} className="block">{l}</span>)}</dd>
        </div>
        <div>
          <dt className="text-white/50">Postadresse</dt>
          <dd className="mt-1 text-white/90">{company.hq.post.map((l) => <span key={l} className="block">{l}</span>)}</dd>
        </div>
        <div>
          <dt className="text-white/50">Avdelingar</dt>
          <dd className="mt-1 text-white/90">{company.branchesText}</dd>
        </div>
        <div>
          <dt className="text-white/50">Telefon</dt>
          <dd className="mt-1"><a href={company.phoneHref} className="u-link tabular font-medium text-white">{company.phone}</a></dd>
        </div>
        <div>
          <dt className="text-white/50">E-post</dt>
          <dd className="mt-1"><a href={`mailto:${company.email}`} className="u-link font-medium text-white">{company.email}</a></dd>
        </div>
      </dl>
    </div>
  </section>
);
